import React, { useState } from "react";
import { FileText, FileSpreadsheet, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

/**
 * Compact export bar showing PDF + CSV buttons.
 * Props:
 *   onExportPdf: () => Promise|void
 *   onExportCsv: () => Promise|void
 *   testidPrefix: string (e.g. "admin-summary")
 */
export default function ExportButtons({ onExportPdf, onExportCsv, testidPrefix = "dash" }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(null); // "pdf" | "csv" | null

  const handle = async (kind, fn, successKey) => {
    setBusy(kind);
    try {
      await fn();
      toast.success(t(successKey));
    } catch (e) {
      console.error(e);
      toast.error(t("dashboard.exportError"));
    } finally {
      setBusy(null);
    }
  };

  const baseCls = "inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1.5 border transition-colors disabled:opacity-60 disabled:cursor-not-allowed";

  return (
    <div className="inline-flex gap-2" data-testid={`${testidPrefix}-export-bar`}>
      <button
        type="button"
        onClick={() => handle("pdf", onExportPdf, "dashboard.pdfSuccess")}
        disabled={busy !== null}
        data-testid={`${testidPrefix}-export-pdf`}
        className={`${baseCls} bg-white text-ink border-sand hover:border-brand-red hover:text-brand-red`}
      >
        {busy === "pdf" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
        {busy === "pdf" ? t("dashboard.exporting") : t("dashboard.exportPdf")}
      </button>
      <button
        type="button"
        onClick={() => handle("csv", onExportCsv, "dashboard.csvSuccess")}
        disabled={busy !== null}
        data-testid={`${testidPrefix}-export-csv`}
        className={`${baseCls} bg-white text-ink border-sand hover:border-brand-green hover:text-brand-green`}
      >
        {busy === "csv" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileSpreadsheet className="w-3.5 h-3.5" />}
        {busy === "csv" ? t("dashboard.exporting") : t("dashboard.exportCsv")}
      </button>
    </div>
  );
}
